import { FC } from 'react';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import PriorityHigh from '@mui/icons-material/PriorityHigh';
import { Box, Typography } from '@mui/material';
import { CHECK_COPY } from '../ReportDialog.constants';
import { ValidateTabProps } from '../ReportDialog.props';
import { reportDialogColors as colors } from '../ReportDialog.styles';
import { formatFailedCompany, marketDataCheckLabel, marketDataFailureDetail } from '../ReportDialog.utils';
import SourceTable from '../SourceTable';

const TONE_COLORS = {
  pass: { solid: colors.pass, wash: colors.passWash },
  warn: { solid: colors.warn, wash: colors.warnWash },
  fail: { solid: colors.fail, wash: colors.failWash },
};

const ValidateTab: FC<ValidateTabProps> = ({ checks = [], marketData, sourceRows }) => (
  <Box data-testid="daily-nav-report-validate">
    {checks.map(check => {
      const { passed } = check;
      // Firm-wide: no companies to list, so its detail comes from market_data.
      const isMarketData = check.key === 'market_data_complete';
      const label = isMarketData ? marketDataCheckLabel(marketData) : (CHECK_COPY[check.key] ?? check.key);
      // Failed (final) and stalled (in flight) runs pass but still warn.
      const isWarning =
        isMarketData && passed && (marketData?.status === 'failed' || marketData?.status === 'stalled');
      const marketDataDetail = isMarketData && (!passed || isWarning) ? marketDataFailureDetail(marketData) : '';
      let tone: keyof typeof TONE_COLORS = passed ? 'pass' : 'fail';
      if (isWarning) {
        tone = 'warn';
      }
      return (
        <Box
          key={check.key}
          data-testid={`daily-nav-validation-${check.key}`}
          sx={{
            alignItems: 'flex-start',
            bgcolor: TONE_COLORS[tone].wash,
            borderRadius: 1.5,
            display: 'flex',
            gap: 1.25,
            mb: 1,
            px: 1.5,
            py: 1.25,
          }}>
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: TONE_COLORS[tone].solid,
              borderRadius: '50%',
              color: 'common.white',
              display: 'flex',
              flexShrink: 0,
              height: 22,
              justifyContent: 'center',
              mt: 0.15,
              width: 22,
            }}>
            {tone === 'pass' && <Check sx={{ fontSize: 16 }} />}
            {tone === 'warn' && <PriorityHigh sx={{ fontSize: 16 }} />}
            {tone === 'fail' && <Close sx={{ fontSize: 16 }} />}
          </Box>
          <Box>
            <Typography sx={{ color: colors.ink, fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>{label}</Typography>
            {marketDataDetail && (
              <Typography
                data-testid="daily-nav-validation-market-data-detail"
                sx={{ color: colors.inkSoft, fontSize: 12, lineHeight: 1.5, mt: 0.5 }}>
                {marketDataDetail}
              </Typography>
            )}
            {!passed && check.companies.length > 0 && (
              <Typography sx={{ color: colors.inkSoft, fontSize: 12, lineHeight: 1.5, mt: 0.5 }}>
                {check.companies.map(company => formatFailedCompany(company, sourceRows)).join('; ')}
              </Typography>
            )}
          </Box>
        </Box>
      );
    })}

    <Typography
      sx={{
        color: colors.inkSoft,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.2,
        mb: 1,
        mt: 2.5,
      }}>
      What was validated
    </Typography>
    <SourceTable rows={sourceRows} />
  </Box>
);

export default ValidateTab;
